// One person's dress choice for the Studio Editor: a garment grid, and a colour popover per garment (main colour, the second
// colours of the garments that have them, and the pattern / head-cover switches). The whole choice is one object — see
// DEFAULT_DRESS / dressFields in studioConstants.js — so the same component serves one person and each of two people.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconCircleOff } from '@tabler/icons-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DressPreviewSvg } from '../../../components/common/DressPreviewSvg';
import { cn } from '@/lib/utils';
import { MAIN_TAB_LABEL, parseDress, secondColorsFor } from './studioConstants';

// Pattern previews on the colour swatches (a checked shirt / suit, a striped panjabi): the colour with the pattern drawn over it.
const isLight = (hex) => {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return false;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    return 0.299 * r + 0.587 * g + 0.114 * b > 180;
};
function patternStyle(kind, hex) {
    const line = isLight(hex) ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.5)';
    const image = kind === 'check'
        ? `repeating-linear-gradient(0deg, ${line} 0 2px, transparent 2px 7px), repeating-linear-gradient(90deg, ${line} 0 2px, transparent 2px 7px)`
        : `repeating-linear-gradient(90deg, ${line} 0 2px, transparent 2px 7px)`;
    return { backgroundColor: hex, backgroundImage: image };
}

function Chip({ active, onToggle, children }) {
    return (
        <button
            type="button"
            aria-pressed={active}
            onClick={onToggle}
            className={cn(
                'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                active ? 'border-primary bg-primary/10 text-primary' : 'border-zinc-200 text-zinc-600 hover:border-primary dark:border-zinc-600 dark:text-zinc-300',
            )}
        >
            {children}
        </button>
    );
}

/**
 * @param dress        the person's dress object (DEFAULT_DRESS shape)
 * @param onChange     (nextDress) => void
 * @param garments     the garments on offer (already filtered by the account's choices / country)
 * @param colors       the colour palette [{ id, label, hex }]
 * @param showSubColors  the account's "alternative clothing colour" setting (suits / sarees offer second colours only then)
 * @param personKey    keeps the open popover of one person apart from the other's
 */
export default function StudioDressPicker({ dress, onChange, garments, colors, showSubColors, disabled = false, personKey = 'p' }) {
    const { t } = useTranslation();
    const [openKey, setOpenKey] = useState(null);
    const [tab, setTab] = useState('main');
    const current = parseDress(dress.value);
    const hexOf = (id) => colors.find((c) => c.id === id)?.hex || null;
    const set = (patch) => onChange({ ...dress, ...patch });

    const openFor = (g) => {
        if (disabled) return;
        if (current.type !== g.value) set({ value: `${g.defaultColor}_${g.value}` });
        setTab('main');
        setOpenKey((k) => (k === `${personKey}_${g.value}` ? null : `${personKey}_${g.value}`));
    };

    // The garment art shows the chosen colours: the main colour, and the second colour on the part that carries it.
    const previewProps = (g) => {
        if (current.type !== g.value) return {};
        const props = { color: hexOf(current.color) };
        if (g.value === 'suite' || g.value === 'suite-tieless') props.shirtColor = hexOf(dress.shirtColor);
        if (g.value === 'suite' || (g.value === 'shirt' && dress.shirtTie)) props.tieColor = hexOf(dress.tieColor);
        if (g.value === 'sharee' || g.value === 'sharee-design') props.blouseColor = hexOf(dress.blouseColor);
        if (g.value === 'kameez-hijab' || g.value === 'shari-hijab') props.blouseColor = hexOf(dress.hijabColor);
        if (g.value === 'orna-kameez') props.blouseColor = hexOf(dress.ornaColor);
        if (g.value === 'thobe-ghutra') props.blouseColor = hexOf(dress.ghutraColor);
        return props;
    };

    const renderPopover = (g) => {
        const second = secondColorsFor(g.value, dress, showSubColors);
        const tabs = second.length ? [{ tab: 'main', label: t(MAIN_TAB_LABEL[g.value] || g.labelKey), colorId: current.color }, ...second.map((c) => ({ tab: c.tab, label: t(c.labelKey), colorId: dress[c.field], field: c.field }))] : null;
        const activeTab = tabs?.find((x) => x.tab === tab) || tabs?.[0] || { tab: 'main' };
        const onMain = activeTab.tab === 'main';
        // The switches of the garments that have them (a checked shirt / suit, the tie, the saree ghomta, an orna over the head, a striped panjabi).
        const switches = [];
        if (onMain && ['shirt', 'suite', 'suite-tieless'].includes(g.value)) switches.push({ key: 'shirtCheck', label: t(g.value === 'shirt' ? 'imageGeneration.checkShirt' : 'imageGeneration.checkSuit') });
        if (g.value === 'shirt') switches.push({ key: 'shirtTie', label: t('imageGeneration.tie') });
        if (g.value === 'panjbi') switches.push({ key: 'panjabiStripe', label: t('imageGeneration.stripePanjabi') });
        if (g.value === 'sharee' || g.value === 'sharee-design') switches.push({ key: 'shareeHead', label: t('studioEditor.shareeHead') });
        if (g.value === 'orna-kameez') switches.push({ key: 'kameezHead', label: t('studioEditor.ornaHead') });
        const patterned = onMain && ((dress.shirtCheck && ['shirt', 'suite', 'suite-tieless'].includes(g.value)) ? 'check' : (dress.panjabiStripe && g.value === 'panjbi') ? 'stripe' : null);
        const isSelected = (c) => (onMain ? current.type === g.value && current.color === c.id : dress[activeTab.field] === c.id);

        return (
            <PopoverContent side="bottom" align="start" collisionPadding={8} className="z-[1000] w-[264px] border-2 border-primary p-3 dark:bg-zinc-900">
                {tabs ? (
                    <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                        {tabs.map((x) => (
                            <button
                                key={x.tab}
                                type="button"
                                onClick={() => setTab(x.tab)}
                                className={cn(
                                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                                    activeTab.tab === x.tab ? 'border-primary bg-primary/10 text-primary' : 'border-zinc-200 text-zinc-600 dark:border-zinc-600 dark:text-zinc-300',
                                )}
                            >
                                <span className="h-2.5 w-2.5 rounded-full border border-black/15" style={{ background: hexOf(x.colorId) || '#aaa' }} />
                                {x.label}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="mb-2.5 text-xs !font-semibold text-primary">{t(g.labelKey)} — {t('imageGeneration.pickColor')}</p>
                )}
                {switches.length > 0 && (
                    <div className="mb-2.5 flex flex-wrap gap-1.5">
                        {switches.map((s) => <Chip key={s.key} active={!!dress[s.key]} onToggle={() => set({ [s.key]: !dress[s.key] })}>{s.label}</Chip>)}
                    </div>
                )}
                <div className="grid grid-cols-6 gap-2">
                    {colors.map((c) => {
                        const selected = isSelected(c);
                        return (
                            <button
                                key={c.id}
                                type="button"
                                title={c.label}
                                onClick={() => {
                                    if (onMain) { set({ value: `${c.id}_${g.value}` }); setOpenKey(null); } else set({ [activeTab.field]: c.id });
                                }}
                                className={cn(
                                    'relative aspect-square rounded-full border transition-shadow',
                                    c.id === 'white' ? 'border-zinc-300' : 'border-black/10',
                                    selected && 'ring-2 ring-primary ring-offset-2 ring-offset-white dark:ring-offset-zinc-900',
                                )}
                                style={patterned ? patternStyle(patterned, c.hex) : { background: c.hex }}
                            >
                                {selected && <span className="absolute inset-0 flex items-center justify-center"><IconCheck size={13} strokeWidth={3} className={isLight(c.hex) ? 'text-zinc-500' : 'text-white'} /></span>}
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        );
    };

    return (
        <div className="grid grid-cols-5 gap-2">
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled}
                        aria-pressed={current.type === 'original'}
                        aria-label={t('imageGeneration.originalClothing')}
                        onClick={() => { set({ value: 'original' }); setOpenKey(null); }}
                        className={cn(
                            'relative flex aspect-square items-center justify-center rounded-xl border-2 bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800',
                            current.type === 'original' ? 'border-primary' : 'border-zinc-200 hover:border-primary dark:border-zinc-700',
                        )}
                    >
                        <IconCircleOff size={26} strokeWidth={2} className="text-zinc-400" />
                        {current.type === 'original' && <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white shadow-sm"><IconCheck size={10} strokeWidth={3.5} /></span>}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.originalClothing')}</TooltipContent>
            </Tooltip>
            {garments.map((g) => {
                const selected = current.type === g.value;
                const activeHex = selected ? hexOf(current.color) : null;
                const key = `${personKey}_${g.value}`;
                return (
                    <Popover key={g.value} open={openKey === key} onOpenChange={(o) => { if (!o) setOpenKey(null); }}>
                        <Tooltip open={openKey === key ? false : undefined}>
                            <TooltipTrigger asChild>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        disabled={disabled}
                                        aria-pressed={selected}
                                        aria-label={t(g.labelKey)}
                                        onClick={() => openFor(g)}
                                        className={cn(
                                            'relative aspect-square rounded-xl border-2 bg-white p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800',
                                            selected ? 'border-primary' : 'border-zinc-200 hover:border-primary dark:border-zinc-700',
                                        )}
                                    >
                                        <DressPreviewSvg
                                            src={g.value === 'shirt' && selected && dress.shirtTie ? '/images/clothing-styles/shirt-tie.svg' : g.image}
                                            alt={t(g.labelKey)}
                                            className="h-full w-full"
                                            {...previewProps(g)}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        {selected && (
                                            <span
                                                className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-primary text-white shadow-sm"
                                                style={activeHex ? { background: activeHex } : undefined}
                                            >
                                                <IconCheck size={9} strokeWidth={3.5} className={['white', 'yellow'].includes(current.color) ? 'text-zinc-400' : 'text-white'} />
                                            </span>
                                        )}
                                    </button>
                                </PopoverTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="z-[2000]">{t(g.labelKey)}</TooltipContent>
                        </Tooltip>
                        {renderPopover(g)}
                    </Popover>
                );
            })}
        </div>
    );
}
